import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { KpiTile } from '@ps/ui-kit/components/kpi-tile';
import { ApiError } from '@ps/api-client/http';
import { staffApi, type InternalApplicationSummary, type StaffApp } from '@ps/api-client/staff';
import { Alert } from '@ps/ui-kit/components/alert';
import { Button } from '@ps/ui-kit/components/button';
import { Checkbox, Select } from '@ps/ui-kit/components/field';
import { SearchBar } from '@ps/ui-kit/components/search-bar';
import { useSearchParam } from '@ps/ui-kit/lib/route';
import { Pill } from '@ps/ui-kit/components/pill';
import { Empty, ErrorState, Loading } from '@ps/ui-kit/components/states';
import { cn } from '@ps/ui-kit/lib/cn';
import { ageDays, countryName, date, titleCase } from '@ps/ui-kit/lib/format';
import { LinkRow, Table } from '@ps/ui-kit/components/table';
import { Card } from '@ps/ui-kit/components/card';
import { BAND_TONE, stateLabel, STATE_BORDER, STATE_TONE } from './labels';

// A-01 Application queue. Filters are applied by the SERVER (cursor-paginated), never client-side on a long list.

const STATES = ['SUBMITTED', 'SCREENING_HIT', 'DOC_REVIEW', 'RFI', 'RECOMMENDED', 'CO_REVIEW', 'ACTIVE', 'REJECTED', 'BLOCKED', 'DRAFT'];
const OPEN_STATES = ['SUBMITTED', 'SCREENING_HIT', 'DOC_REVIEW', 'RFI', 'RECOMMENDED', 'CO_REVIEW'];
const SLA_DAYS = 5;

export function ApplicationQueue({ app, me, canAssign, presetStates = OPEN_STATES, title = 'Application queue', onOpen }: {
  app: StaffApp; me?: string; canAssign: boolean; presetStates?: string[]; title?: string; onOpen: (id: string) => void;
}) {
  const api = React.useMemo(() => staffApi(app), [app]);
  const qc = useQueryClient();
  // Filters live in the address, so opening an application and coming back keeps them (and a refresh or a link too).
  const [statesText, setStatesText] = useSearchParam('states', presetStates.join(','));
  const states = statesText ? statesText.split(',') : [];
  const setStates = (v: string[]) => setStatesText(v.join(','));
  const [band, setBand] = useSearchParam('band', '');
  const [screening, setScreening] = useSearchParam('screening', '');
  const [mineText, setMine] = useSearchParam('mine', '');
  const mine = mineText === '1';
  const [search, setSearch] = useSearchParam('q', '');
  const [term, setTerm] = React.useState(search.trim());
  const [selected, setSelected] = React.useState<string[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  React.useEffect(() => { const t = setTimeout(() => setTerm(search.trim()), 300); return () => clearTimeout(t); }, [search]);

  const filters = { states: states.join(','), bands: band, screening, search: term, assignedTo: mine ? me : undefined };
  const stats = useQuery({ queryKey: ['applications', app, 'stats'], queryFn: api.stats });
  const list = useInfiniteQuery({
    queryKey: ['applications', app, filters],
    queryFn: ({ pageParam }) => api.list({ ...filters, cursor: pageParam, limit: '50' }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: last => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap(p => p.items) ?? [];

  async function assign(assigneeId: string | null) {
    setError(null);
    try { await api.assign(selected, assigneeId); setSelected([]); await qc.invalidateQueries({ queryKey: ['applications', app] }); }
    catch (e) { setError(e instanceof ApiError ? e.body.message : 'Something went wrong.'); }
  }

  return (
    <div>
      <h1 className="font-display text-xl font-bold text-ink">{title}</h1>
      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[['Open applications', stats.data?.open], ['Screening hits (CO action)', stats.data?.screeningHits], ['Awaiting customer', stats.data?.awaitingCustomer],
          ['Median time to decision', stats.data ? (stats.data.medianDaysToDecision === null ? '—' : `${stats.data.medianDaysToDecision} d`) : undefined]].map(([label, value]) => (
          <KpiTile key={label as string} value={value ?? '…'} label={label} />
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-end gap-3">
        <SearchBar label="Search reference or applicant name" placeholder="Search reference or name" value={search} onChange={setSearch} className="min-w-60 flex-1" />
        <Select aria-label="Risk band" value={band} onChange={e => setBand(e.target.value)} className="w-40"><option value="">All bands</option><option>LOW</option><option>STANDARD</option><option>HIGH</option></Select>
        <Select aria-label="Screening" value={screening} onChange={e => setScreening(e.target.value)} className="w-44"><option value="">Any screening</option><option value="CLEAR">Clear</option><option value="HIT_OPEN">Hit open</option></Select>
        {me && <Checkbox label="Assigned to me" className="h-11 items-center" checked={mine} onChange={e => setMine(e.target.checked ? '1' : '')} />}
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5" role="group" aria-label="States">
        {STATES.map(s => {
          const on = states.includes(s);
          return <button key={s} type="button" aria-pressed={on} onClick={() => setStates(on ? states.filter(x => x !== s) : [...states, s])}
            className={cn('rounded-full border px-2.5 py-0.5 text-xs font-semibold uppercase', on ? 'border-tick bg-mint-2 text-tick-ink' : 'border-line bg-surface text-ink-3')}>{stateLabel(s)}</button>;
        })}
      </div>

      {canAssign && selected.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg bg-mint-2 p-3 text-sm">
          <span className="font-semibold">{selected.length} selected</span>
          {me && <Button size="sm" onClick={() => assign(me)}>Assign to me</Button>}
          <Button size="sm" variant="secondary" onClick={() => assign(null)}>Unassign</Button>
        </div>
      )}
      {error && <Alert tone="bad" className="mt-4">{error}</Alert>}

      <Card className="mt-4 overflow-x-auto p-0">
        {list.isPending ? <Loading /> : list.error ? <div className="p-4"><ErrorState message="We couldn't load the queue." onRetry={() => list.refetch()} /></div> : !rows.length ? <div className="p-4"><Empty title="No applications match these filters" /></div> : (
          <Table bare>
            <thead>
              <tr>{canAssign && <th className="w-8"><span className="sr-only">Select</span></th>}<th>Reference</th><th>Applicant</th><th>Type</th><th>Submitted</th><th>State</th><th>Risk</th><th>Screening</th><th>Owner</th><th className="text-right">Age</th></tr>
            </thead>
            <tbody>{rows.map(r => <Row key={r.id} r={r} canAssign={canAssign} selected={selected.includes(r.id)} onSelect={on => setSelected(on ? [...selected, r.id] : selected.filter(x => x !== r.id))} onOpen={() => onOpen(r.id)} />)}</tbody>
          </Table>
        )}
      </Card>
      {list.hasNextPage && <Button variant="secondary" className="mt-4" onClick={() => list.fetchNextPage()} disabled={list.isFetchingNextPage}>{list.isFetchingNextPage ? 'Loading…' : 'Load more'}</Button>}
    </div>
  );
}

function Row({ r, canAssign, selected, onSelect, onOpen }: { r: InternalApplicationSummary; canAssign: boolean; selected: boolean; onSelect: (on: boolean) => void; onOpen: () => void }) {
  const age = ageDays(r.submittedAt);
  const late = age !== null && age > SLA_DAYS && !['ACTIVE', 'REJECTED', 'BLOCKED'].includes(r.state);
  return (
    <LinkRow className={cn('border-l-4', STATE_BORDER[r.state] ?? 'border-l-transparent')} onOpen={onOpen}>
      {canAssign && <td onClick={e => e.stopPropagation()}><Checkbox aria-label={`Select ${r.reference}`} checked={selected} onChange={e => onSelect(e.target.checked)} /></td>}
      <td className="font-mono text-xs"><button className="hover:underline" onClick={onOpen}>{r.reference}</button></td>
      <td><span className="font-semibold text-ink">{r.legalName}</span><span className="block text-xs text-ink-3">{countryName(r.country)} · {r.industry}</span></td>
      <td>{titleCase(r.type)}</td>
      <td>{date(r.submittedAt)}</td>
      <td><Pill tone={STATE_TONE[r.state] ?? 'neu'} upper>{stateLabel(r.state)}</Pill></td>
      <td>{r.riskBand ? <Pill tone={BAND_TONE[r.riskBand] ?? 'neu'} upper>{r.riskBand}</Pill> : <span className="text-ink-3">—</span>}</td>
      <td>{r.openHits ? <Pill tone="bad">{r.openHits} possible match{r.openHits > 1 ? 'es' : ''}</Pill> : r.pep ? <Pill tone="warn" upper>PEP</Pill> : ['DRAFT', 'SUBMITTED'].includes(r.state) ? <span className="text-ink-3">—</span> : <span className="text-ok">Clear</span>}</td>
      <td className="text-ink-2">{r.assignedToName ?? <span className="text-ink-3">—</span>}</td>
      <td className={cn('text-right tabular', late && 'font-semibold text-bad')}>{age === null ? '—' : `${age} d`}</td>
    </LinkRow>
  );
}
